import React, { useState, useCallback } from 'react';
import theme from './FileUploadWizard.module.scss';
import { FileUpload, Modal, ModalBody, ModalFooter, ModalHeader, LoadingOverlay } from '@ygtechnologies/bygr-frontend-component-library';
import { FileData } from '@ygtechnologies/rubus-frontend-utilities';
import { Button } from '@/components/Buttons';
import { Trans } from '@lingui/react/macro';
import FileUploadUnitDocuments from './FileUploadUnitDocuments/FileUploadUnitDocuments';
import { getOpenDocumentUrl } from '@/services/documents';
import { triggerHandler } from '@ygtechnologies/rubus-frontend-utilities';
import { LargeRadio } from '@/components/forms/Radio';

export interface ChatFileUploadWizardProps {
    isOpen: boolean;
    onFileChange(files: FileData[]): void;
    toggle(): void;
    initialFileData?: FileData | null;
    residentialUnitId?: number;
    disableDocumentAccess?: boolean;
}
enum Step {
    Intro,
    UnitFileSelect,
}

export const ChatFileUploadWizard: React.FC<ChatFileUploadWizardProps> = ({
    isOpen,
    onFileChange,
    toggle,
    residentialUnitId,
    disableDocumentAccess = false,
}) => {
    const [selectedVersionId, setSelectedVersionId] = useState<null | number>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [uploadMode, setUploadMode] = useState<'filesystem' | 'document'>('filesystem');

    const reset = () => {
        setIsLoading(false);
        setSelectedVersionId(null);
        setUploadMode('filesystem');
    };

    const handleOpen = () => {
        reset();
    };

    const handleClose = () => {
        toggle();
    };

    const handleFileSubmit = async (files: File[]) => {
        setIsLoading(true);
        const droppedFileData = files.filter((x) => x.size > 0).map((x) => new FileData(x));
        triggerHandler(onFileChange, droppedFileData);
        toggle();
        setIsLoading(false);
    };

    const handleDocumentSubmit = async () => {
        setIsLoading(true);

        if (!selectedVersionId) {
            return;
        }
        const file = await FileData.download(getOpenDocumentUrl(selectedVersionId));

        triggerHandler(onFileChange, [file]);
        toggle();
        setIsLoading(false);
    };

    return (
        <Modal
            onOpen={handleOpen}
            className={theme.modal}
            size="lg"
            isOpen={isOpen}
            toggle={handleClose}>
            <ModalHeader>
                <Trans>Upload an attachment</Trans>
            </ModalHeader>
            <ModalBody>
                <LargeRadio onChange={() => setUploadMode('filesystem')} checked={uploadMode === 'filesystem'}>
                    <Trans>
                        <strong>Upload from this computer</strong>
                        Find and upload a file from your computer.
                    </Trans>
                </LargeRadio>
                {residentialUnitId && (
                    <LargeRadio
                        disabled={disableDocumentAccess}
                        onChange={() => setUploadMode('document')}
                        checked={uploadMode === 'document'}>
                        <Trans>
                            <strong>Upload from unit documents</strong>
                            Find and upload an existing unit document, to browse and select the document version you want,
                            click on 'Show details'.
                        </Trans>
                    </LargeRadio>
                )}

                {uploadMode === 'document' && (
                    <FileUploadUnitDocuments
                        selectedVersionId={selectedVersionId}
                        onSelect={(versionId) => setSelectedVersionId(versionId)}
                        residentialUnitId={residentialUnitId || 0}
                    />
                )}
            </ModalBody>

            <ModalFooter>
                {uploadMode === 'filesystem' ? (
                    <FileUpload unstyled single onDrop={handleFileSubmit} containerClassName={theme.fileUploadButton}>
                        <Button color="primary" loading={isLoading}>
                            <Trans>Choose file</Trans>
                        </Button>
                    </FileUpload>
                ) : (
                    <Button color="primary" onClick={handleDocumentSubmit} loading={isLoading}>
                        <Trans>Submit</Trans>
                    </Button>
                )}

                <Button color="tertiary" onClick={handleClose} disabled={isLoading}>
                    <Trans>Cancel</Trans>
                </Button>
            </ModalFooter>
        </Modal>
    );
};
